import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Trefferbild: zeigt je Ziel, wo die am Board eingegebenen Pfeile gelandet sind.
 * Nur Persönliches Training. Liga, Challenges und Online-Games nutzen das nicht.
 *
 * Geometrie und Farben sind eine bewusste Kopie aus ScoringBoard.tsx. Jene Datei
 * ist die Eingabe im Training und darf für diese reine Anzeige nicht verändert werden.
 */

const CX = 280, CY = 280, R_NUM = 258, R_DO = 230, R_DI = 219, R_TO = 145, R_TI = 134, R_BO = 22, R_BI = 9;
const DARK = '#15181d', LIGHT = '#e2d3b0', RED = '#bf3b34', GREEN = '#2f9e63', WIRE = '#0a0e14';
const ORDER = [20, 1, 18, 4, 13, 6, 10, 15, 2, 17, 3, 19, 7, 16, 8, 11, 14, 9, 12, 5];
const CM = (170 / 230) / 10;   // ein Pixel in Zentimetern auf einem echten Board
const MIN = 10;                // ab so vielen Pfeilen zeigen wir Zahlen

type Ring = 'segment' | 'double' | 'triple';
type Dart = { x: number; y: number; seg: number; ring: Ring; tag: number };

export const pt = (r: number, d: number): [number, number] => {
  const a = ((d - 90) * Math.PI) / 180;
  return [CX + r * Math.cos(a), CY + r * Math.sin(a)];
};
export const wedge = (r1: number, r2: number, a1: number, a2: number) => {
  const [x1, y1] = pt(r2, a1), [x2, y2] = pt(r2, a2), [x3, y3] = pt(r1, a2), [x4, y4] = pt(r1, a1);
  return `M ${x1} ${y1} A ${r2} ${r2} 0 0 1 ${x2} ${y2} L ${x3} ${y3} A ${r1} ${r1} 0 0 0 ${x4} ${y4} Z`;
};
const ringOf = (r: number) => {
  if (r < R_BI) return 'bull50';
  if (r < R_BO) return 'bull25';
  if (r < R_TI) return 'single';
  if (r < R_TO) return 'triple';
  if (r < R_DI) return 'single';
  if (r < R_DO) return 'double';
  return 'out';
};
const segOf = (x: number, y: number) =>
  ORDER[Math.round(((((Math.atan2(y - CY, x - CX) * 180) / Math.PI) + 90 + 360) % 360) / 18) % 20];
const aimPunkt = (seg: number, ring: Ring): [number, number] => {
  if (seg === 25) return [CX, CY];
  const i = ORDER.indexOf(seg);
  if (ring === 'segment') return pt((R_BO + R_DI) / 2, i * 18);
  const rr = ring === 'double' ? (R_DI + R_DO) / 2 : (R_TI + R_TO) / 2;
  return pt(rr, i * 18);
};
const trifft = (d: Dart, ring: Ring, z: number) => {
  const rr = Math.hypot(d.x - CX, d.y - CY), rg = ringOf(rr), sg = segOf(d.x, d.y);
  if (z === 25) return rg === 'bull50' || rg === 'bull25';
  return ring === 'segment' ? sg === z && rg !== 'out' : sg === z && rg === ring;
};

/** Das Board wird einmal gezeichnet, der Ausschnitt entsteht über die viewBox. */
export function BoardGrafik() {
  const teile: JSX.Element[] = [];
  teile.push(<circle key="rim" cx={CX} cy={CY} r={R_NUM + 8} fill="#04060a" />);
  teile.push(<circle key="face" cx={CX} cy={CY} r={R_NUM} fill="#141b24" />);
  for (let i = 0; i < 20; i++) {
    const c = i * 18, a1 = c - 9, a2 = c + 9, dk = i % 2 === 0;
    const s = dk ? DARK : LIGHT, r = dk ? RED : GREEN;
    teile.push(<path key={`s1${i}`} d={wedge(R_BO, R_TI, a1, a2)} fill={s} stroke={WIRE} strokeWidth={1} />);
    teile.push(<path key={`t${i}`} d={wedge(R_TI, R_TO, a1, a2)} fill={r} stroke={WIRE} strokeWidth={1} />);
    teile.push(<path key={`s2${i}`} d={wedge(R_TO, R_DI, a1, a2)} fill={s} stroke={WIRE} strokeWidth={1} />);
    teile.push(<path key={`d${i}`} d={wedge(R_DI, R_DO, a1, a2)} fill={r} stroke={WIRE} strokeWidth={1} />);
  }
  teile.push(<circle key="b25" cx={CX} cy={CY} r={R_BO} fill={GREEN} stroke={WIRE} />);
  teile.push(<circle key="b50" cx={CX} cy={CY} r={R_BI} fill={RED} stroke={WIRE} />);
  for (let i = 0; i < 20; i++) {
    const [x, y] = pt((R_NUM + R_DO) / 2, i * 18);
    teile.push(
      <text key={`n${i}`} x={x} y={y} textAnchor="middle" dominantBaseline="central" fontSize={19} fontWeight={600} fill="#cbb896">
        {ORDER[i]}
      </text>
    );
  }
  return <>{teile}</>;
}

const RING_LABEL: Record<Ring, string> = { segment: 'Feld', double: 'Doppel', triple: 'Triple' };

export const BoardTrefferbild: React.FC = () => {
  const [darts, setDarts] = useState<Dart[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [ring, setRing] = useState<Ring>('double');
  const [zahl, setZahl] = useState(20);
  const [tage, setTage] = useState(0);        // 0 = alle, sonst Anzahl Trainings
  const [zoom, setZoom] = useState<'voll' | 'nah'>('voll');

  useEffect(() => {
    let abgebrochen = false;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth?.user?.id;
      if (!uid) { if (!abgebrochen) setLaedt(false); return; }
      const { data, error } = await supabase
        .from('training_board_darts' as any)
        .select('target_segment, target_multiplier, hit_x, hit_y, created_at, session_id')
        .eq('user_id', uid)
        .not('target_segment', 'is', null)
        .order('created_at', { ascending: false })
        .limit(5000);
      if (abgebrochen) return;
      if (error || !data) { setLaedt(false); return; }

      // Eine Einheit ist die session_id. Ältere Pfeile haben noch keine,
      // für die gilt weiterhin der Trainingstag.
      const einheiten: string[] = [];
      const rows: Dart[] = [];
      for (const r of data as any[]) {
        const x = Number(r.hit_x), y = Number(r.hit_y), seg = Number(r.target_segment);
        if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(seg)) continue;
        const rg: Ring = r.target_multiplier === 'double' ? 'double' : r.target_multiplier === 'triple' ? 'triple' : 'segment';
        const schluessel = r.session_id
          ? `S${r.session_id}`
          : `T${new Date(r.created_at).toLocaleDateString('de-AT')}`;
        let i = einheiten.indexOf(schluessel);
        if (i === -1) { einheiten.push(schluessel); i = einheiten.length - 1; }
        rows.push({ x, y, seg, ring: rg, tag: i });
      }
      setDarts(rows);
      setLaedt(false);
    })();
    return () => { abgebrochen = true; };
  }, []);

  const imZeitraum = useMemo(
    () => (tage === 0 ? darts : darts.filter((d) => d.tag < tage)),
    [darts, tage]
  );
  const fuer = (r: Ring, z: number, list: Dart[] = imZeitraum) => list.filter((d) => d.ring === r && d.seg === z);

  // Auf ein Ziel springen, auf das im gewählten Zeitraum wirklich geworfen wurde
  useEffect(() => {
    if (!darts.length) return;
    if (fuer(ring, zahl).length) return;
    let best = 0, bestN = 0;
    for (const z of [...Array(20).keys()].map((i) => i + 1).concat(25)) {
      const n = fuer(ring, z).length;
      if (n > bestN) { bestN = n; best = z; }
    }
    if (best) setZahl(best);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ring, tage, darts]);

  const pts = fuer(ring, zahl);
  const idx = ORDER.indexOf(zahl);
  const [tx, ty] = aimPunkt(zahl, ring);
  const W = ring === 'segment' ? 150 : 105;
  const viewBox = zoom === 'nah' ? `${tx - W} ${ty - W} ${2 * W} ${2 * W}` : '0 0 560 560';

  const quoteVon = (list: Dart[], z: number) => {
    if (!list.length) return null;
    let t = 0;
    for (const d of list) if (trifft(d, ring, z)) t++;
    return { n: list.length, treffer: t, q: Math.round((t / list.length) * 100) };
  };
  const jetzt = quoteVon(pts, zahl);
  const genug = pts.length >= MIN;

  // Durchschnitt der Pfeile
  const mx = pts.length ? pts.reduce((s, p) => s + p.x, 0) / pts.length : tx;
  const my = pts.length ? pts.reduce((s, p) => s + p.y, 0) / pts.length : ty;

  // seitlicher Versatz bei ganzen Zahlen: zur Mittellinie auf Höhe des Pfeils
  const seitlich = pts.map((p) => {
    const rr = Math.hypot(p.x - CX, p.y - CY);
    const [ax] = pt(rr, idx * 18);
    return p.x - ax;
  });
  const seitMittel = seitlich.length ? seitlich.reduce((a, b) => a + b, 0) / seitlich.length : 0;
  const seitSort = seitlich.map(Math.abs).sort((a, b) => a - b);
  const seit67 = seitSort.length ? seitSort[Math.min(seitSort.length - 1, Math.floor(seitSort.length * 0.67))] * CM : 0;

  // Einordnung: die übrigen Ziele desselben Rings
  const uebrige = useMemo(() => {
    const list = imZeitraum.filter((d) => d.ring === ring && d.seg !== zahl);
    if (list.length < MIN) return null;
    let t = 0;
    for (const d of list) if (trifft(d, ring, d.seg)) t++;
    return { n: list.length, q: Math.round((t / list.length) * 100) };
  }, [imZeitraum, ring, zahl]);

  // Vergleich zum Training davor
  const vergleich = useMemo(() => {
    if (tage !== 1 || !genug) return null;
    const davorListe = fuer(ring, zahl, darts.filter((d) => d.tag === 1));
    const davor = quoteVon(davorListe, zahl);
    if (!davor || davor.n < MIN) return { zuwenig: davor ? davor.n : 0 };
    return { davor: davor.q, jetzt: jetzt ? jetzt.q : 0 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tage, genug, ring, zahl, darts, jetzt]);

  const zielName = `${RING_LABEL[ring]} ${zahl === 25 ? 'Bull' : zahl}`;
  const zahlenListe = [...Array(20).keys()].map((i) => i + 1);

  const ringPlural = ring === 'double' ? 'Doppel' : ring === 'segment' ? 'Zahlen' : 'Triples';

  /* Darstellung nach Vorlage 07_statistik-auszeichnungen/statistik_persoenlichesTraining_* (P9): Panel mit Ring/Zeitraum,
     Zahlenleiste, Board mit Zoom und Legende, Kennzahl und Hinweise. Berechnungen oben unverändert. */
  const kopf = (
    <div className="aus-panel-head">
      <div className="aus-tb-intro"><h3>Trefferbild</h3>
        <p>Wo deine Pfeile rund um ein Ziel landen – aus allen Pfeilen, die du im Training auf dem Dartboard getippt hast. Checkout-Spiele sind nicht dabei.</p></div>
      {!laedt && darts.length > 0 && (
        <div className="aus-tb-controls">
          <div className="aus-seg" role="group" aria-label="Ring">
            {(['segment', 'double', 'triple'] as Ring[]).map((r) => (
              <button key={r} type="button" aria-pressed={ring === r} onClick={() => setRing(r)}>{RING_LABEL[r]}</button>
            ))}
          </div>
          <div className="aus-seg" role="group" aria-label="Trainings">
            {([[1, 'Letztes Training'], [30, 'Letzte 30'], [0, 'Alle']] as const).map(([v, l]) => (
              <button key={v} type="button" aria-pressed={tage === v} onClick={() => setTage(v)}>{l}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  if (laedt) return <section className="aus-panel aus-board-panel" aria-label="Trefferbild im persönlichen Training">{kopf}<p className="aus-muted">Trefferbild wird geladen …</p></section>;

  if (!darts.length) {
    return (
      <section className="aus-panel aus-board-panel" aria-label="Trefferbild im persönlichen Training">
        {kopf}
        <div className="aus-tb-none">
          Noch keine Pfeile am Board. Das Trefferbild entsteht aus Pfeilen, die du im Training direkt auf dem Dartboard antippst – diese Eingabe gibt es am Computer ab 1280 Pixel Fensterbreite.
        </div>
      </section>
    );
  }

  const fakten: { t: string; s: string }[] = [];
  if (genug && jetzt) {
    if (ring === 'segment') {
      fakten.push({
        t: Math.abs(seitMittel * CM) >= 1
          ? `Im Schnitt ${Math.abs(seitMittel * CM).toFixed(0)} cm ${seitMittel > 0 ? 'rechts' : 'links'} von der Mitte`
          : 'Im Schnitt mittig auf der Zahl',
        s: 'Gemessen an der gestrichelten Mittellinie. Ob der Pfeil oben oder unten in der Zahl steckt, ist kein Fehler.',
      });
      fakten.push({ t: `2 von 3 Pfeilen höchstens ${seit67.toFixed(0)} cm daneben`, s: 'Seitlich zur Mittellinie gerechnet.' });
    } else {
      const dx = mx - tx, dy = my - ty, teile: string[] = [];
      if (Math.abs(dx * CM) >= 1) teile.push(`${Math.abs(dx * CM).toFixed(0)} cm ${dx > 0 ? 'rechts' : 'links'}`);
      if (Math.abs(dy * CM) >= 1) teile.push(`${Math.abs(dy * CM).toFixed(0)} cm ${dy > 0 ? 'unter' : 'über'} dem Ziel`);
      fakten.push({
        t: teile.length ? `Im Schnitt ${teile.join(' und ')}` : 'Im Schnitt mittig auf dem Ziel',
        s: 'Der weiße Punkt ist der Durchschnitt deiner Pfeile, das Kreuz die Mitte des Ziels.',
      });
    }
    if (uebrige) {
      fakten.push({
        t: jetzt.q >= uebrige.q ? `${jetzt.q - uebrige.q} Punkte besser als deine übrigen ${ringPlural}` : `${uebrige.q - jetzt.q} Punkte unter deinen übrigen ${ringPlural}`,
        s: `Auf alle anderen Ziele dieses Rings triffst du ${uebrige.q} %.`,
      });
    }
    if (vergleich && 'davor' in vergleich) {
      fakten.push({
        t: 'Verglichen mit dem Training davor',
        s: `Beim Training davor hast du ${vergleich.davor} Prozent getroffen, jetzt sind es ${vergleich.jetzt}. ${
          vergleich.jetzt! > vergleich.davor! ? 'Das ist besser als davor.' : vergleich.jetzt! < vergleich.davor! ? 'Das ist weniger als davor.' : 'Das ist genau gleich.'}`,
      });
    }
    if (vergleich && 'zuwenig' in vergleich) {
      fakten.push({
        t: 'Kein Vergleich möglich',
        s: `Beim Training davor hast du auf dieses Ziel nur ${vergleich.zuwenig} Pfeile geworfen. Für einen Vergleich brauchen wir mindestens ${MIN}.`,
      });
    }
  } else {
    fakten.push({
      t: pts.length ? `${pts.length} ${pts.length === 1 ? 'Pfeil' : 'Pfeile'} auf ${zielName}` : `Noch keine Pfeile auf ${zielName}`,
      s: `Ab ${MIN} Pfeilen zeigen wir dir Zahlen dazu – vorher ist der Durchschnitt zu ungenau.`,
    });
  }

  return (
    <section className="aus-panel aus-board-panel" aria-label="Trefferbild im persönlichen Training">
      {kopf}
      <div className="aus-tb-nums">
        {zahlenListe.concat(ring === 'double' ? [25] : []).map((z) => {
          const list = fuer(ring, z);
          const q = list.length >= MIN ? quoteVon(list, z)!.q : null;
          return (
            <button key={z} type="button" onClick={() => setZahl(z)} aria-pressed={zahl === z}
              title={q === null ? undefined : `${q} Prozent getroffen`} className={list.length ? '' : 'is-empty'}>
              <b>{z === 25 ? 'Bull' : z}</b>
              <span>{list.length || '—'}</span>
              <span className="aus-tb-bar"><i style={{ width: `${q ?? 0}%` }} /></span>
            </button>
          );
        })}
      </div>

      <div className="aus-tb-grid">
        <div className="aus-tb-left">
          <div className="aus-tb-board">
            <svg viewBox={viewBox} xmlns="http://www.w3.org/2000/svg">
              <BoardGrafik />
              {zahl === 25 ? (
                <circle cx={CX} cy={CY} r={R_BO} fill="rgba(52,210,122,.28)" stroke="#34d27a" strokeWidth={2.5} />
              ) : (
                <path
                  d={
                    ring === 'segment'
                      ? wedge(R_BO, R_DI, idx * 18 - 9, idx * 18 + 9)
                      : wedge(ring === 'double' ? R_DI : R_TI, ring === 'double' ? R_DO : R_TO, idx * 18 - 9, idx * 18 + 9)
                  }
                  fill="rgba(52,210,122,.28)"
                  stroke="#34d27a"
                  strokeWidth={2.5}
                />
              )}
              {pts.map((p, i) => {
                const drin = zoom === 'voll' || (Math.abs(p.x - tx) < W && Math.abs(p.y - ty) < W);
                if (!drin) return null;
                return (
                  <circle key={i} cx={p.x} cy={p.y} r={zoom === 'voll' ? 3.4 : 4}
                    fill={trifft(p, ring, zahl) ? '#34d27a' : '#e9eff6'} stroke="#04060a" strokeWidth={1.2} />
                );
              })}
              {genug && ring !== 'segment' && (
                <>
                  <line x1={tx} y1={ty} x2={mx} y2={my} stroke="#fff" strokeOpacity={0.55} strokeWidth={1.6} />
                  <line x1={tx - 9} y1={ty} x2={tx + 9} y2={ty} stroke="#fff" strokeWidth={2} />
                  <line x1={tx} y1={ty - 9} x2={tx} y2={ty + 9} stroke="#fff" strokeWidth={2} />
                  <circle cx={mx} cy={my} r={5.5} fill="#fff" stroke="#04060a" strokeWidth={1.6} />
                </>
              )}
              {genug && ring === 'segment' && (
                <line
                  x1={pt(R_BO, idx * 18)[0]} y1={pt(R_BO, idx * 18)[1]}
                  x2={pt(R_DI, idx * 18)[0]} y2={pt(R_DI, idx * 18)[1]}
                  stroke="#fff" strokeOpacity={0.9} strokeWidth={2} strokeDasharray="6 5"
                />
              )}
            </svg>
          </div>
          <div className="aus-tb-under">
            <div className="aus-seg" role="group" aria-label="Zoom">
              <button type="button" aria-pressed={zoom === 'voll'} onClick={() => setZoom('voll')}>Ganzes Board</button>
              <button type="button" aria-pressed={zoom === 'nah'} onClick={() => setZoom('nah')}>Nah dran</button>
            </div>
            <div className="aus-tb-legend">
              <span><i className="is-hit" />getroffen</span>
              <span><i />daneben</span>
              <span>{ring === 'segment' ? 'gestrichelt: Mitte der Zahl' : 'Kreuz: Ziel · weißer Punkt: Ø deiner Pfeile'}</span>
            </div>
          </div>
        </div>

        <div className="aus-tb-facts">
          <div className="aus-tb-target">
            <small>{pts.length} Pfeile auf {zielName}</small>
            <div><b>{genug && jetzt ? `${jetzt.q} %` : '–'}</b><span>{genug && jetzt ? `${jetzt.treffer} getroffen` : 'zu wenig Pfeile'}</span></div>
          </div>
          {fakten.map((f) => (
            <div key={f.t} className="aus-tb-fact"><b>{f.t}</b><span>{f.s}</span></div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BoardTrefferbild;
